import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Правка заметки — одна форма для раздела «Заметки» и для панели на карточке
// сущности. Раньше каждый из них разворачивал собственный инлайн-редактор:
// разные кнопки, разная обработка ошибок, в гриде текст правился прямо в ячейке.
import { useEffect, type ReactElement } from 'react';
import { Alert, Form, Input, Modal } from 'antd';

import type { NoteItem } from '../api/notes.client.js';
import { useUpdateNoteMutation } from '../hooks/useNotes.js';

interface NoteFormValues {
  body: string;
}

interface NoteFormModalProps {
  open: boolean;
  onClose: () => void;
  note: NoteItem;
}

export function NoteFormModal({ open, onClose, note }: NoteFormModalProps): ReactElement {
  useI18nSubscription();
  const [form] = Form.useForm<NoteFormValues>();
  const updateMut = useUpdateNoteMutation();

  useEffect(() => {
    if (!open) return;
    updateMut.reset();
    form.setFieldsValue({ body: note.body });
  }, [open, note, form]);

  const handleSubmit = (values: NoteFormValues): void => {
    updateMut.mutate(
      { id: note.id, input: { body: values.body.trim() } },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Modal
      title={i18n.t('crm.features.features.notes.components.noteformmodal.редактировать_заметку')}
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={i18n.t('crm.features.features.notes.components.noteformmodal.сохранить')}
      cancelText={i18n.t('crm.features.features.notes.components.noteformmodal.отмена')}
      confirmLoading={updateMut.isPending}
      width={520}
      destroyOnHidden
    >
      {updateMut.isError ? (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          title={i18n.t(
            'crm.features.features.notes.components.noteformmodal.не_удалось_сохранить_заметку',
          )}
          description={(updateMut.error as Error).message}
        />
      ) : null}
      <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={updateMut.isPending}>
        <Form.Item
          name="body"
          label={i18n.t('crm.features.features.notes.components.noteformmodal.текст')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: i18n.t(
                'crm.features.features.notes.components.noteformmodal.заметка_не_может_быть_пустой',
              ),
            },
          ]}
        >
          <Input.TextArea rows={6} maxLength={10_000} autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  );
}
